/**
 * 计算折扣价并保持原始格式
 * @param {string} originalPrice 原始价格字符串（如 "$319.90 USD"）
 * @param {number} discount 折扣比例（0-1之间的小数，如0.2表示20%折扣）
 * @returns {string} 折扣后的价格字符串
 */
function calculateDiscountedPrice(originalPrice, discount) {
  // 币种处理器配置表（按优先级排序）
  const currencyHandlers = [
    // 欧元 €284,90 EUR
    {
      pattern: /€\s*([\d.,]+)\s*EUR/,
      parse: numStr => parseFloat(numStr.replace(/\./g, '').replace(',', '.')),
      format: num =>
        `€${num
          .toFixed(2)
          .replace('.', ',')
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} EUR`
    },

    // 美元 \$319.90 USD
    {
      pattern: /\$([\d,]+(\.\d+)?)\s*USD/,
      parse: numStr => parseFloat(numStr.replace(/,/g, '')),
      format: num => `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} USD`
    },

    // 捷克克朗 7.261,00 Kč
    {
      pattern: /([\d\s.,]+)\s*Kč/,
      parse: numStr => parseFloat(numStr.replace(/\s/g, '').replace(/\./g, '').replace(',', '.')),
      format: num =>
        `${num
          .toFixed(2)
          .replace('.', ',')
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} Kč`
    },

    // 波兰兹罗提 1.210,00 zł
    {
      pattern: /([\d\s.,]+)\s*zł\s*(PLN)?/, // 匹配带或不带PLN的格式
      parse: numStr => parseFloat(numStr.replace(/\s/g, '').replace(/\./g, '').replace(',', '.')),
      format: num =>
        `${num
          .toFixed(2)
          .replace('.', ',') // 小数点替换为逗号
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} zł PLN` // 千分位用点号分隔
    },

    // 日元 ¥22,210 JPY
    {
      pattern: /¥([\d,]+)\s*JPY/,
      parse: numStr => parseFloat(numStr.replace(/,/g, '')),
      format: num => `¥${num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} JPY`
    },

    // 匈牙利福林 116.600 Ft
    {
      pattern: /([\d\s.]+)\s*Ft/,
      parse: numStr => parseFloat(numStr.replace(/\s/g, '').replace(/\./g, '')),
      format: num => `${num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} Ft` // 点号分隔
    },

    // 英镑 £1,447.00 GBP
    {
      pattern: /£([\d,]+(\.\d+)?)\s*GBP/,
      parse: numStr => parseFloat(numStr.replace(/,/g, '')),
      format: num => `£${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} GBP`
    },

    // 丹麦克朗 5.690,00 DKK
    {
      pattern: /([\d\s.,]+)\s*DKK/,
      parse: numStr => parseFloat(numStr.replace(/\s/g, '').replace(/\./g, '').replace(',', '.')),
      format: num =>
        `${num
          .toFixed(2)
          .replace('.', ',')
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} DKK`
    },

    // 罗马尼亚列伊 1.447,00 RON
    {
      pattern: /([\d\s.,]+)\s*(lei\s*)?RON/,
      parse: numStr => parseFloat(numStr.replace(/\s/g, '').replace(/\./g, '').replace(',', '.')),
      format: num =>
        `${num
          .toFixed(2)
          .replace('.', ',')
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} lei RON` // 添加lei
    },

    // 瑞典克朗 3 251 SEK
    {
      pattern: /([\d\s]+(\.\d+)?)\s*SEK/,
      parse: numStr => parseFloat(numStr.replace(/\s/g, '')),
      format: num => `${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} SEK`
    },

    // 澳元 \$334.90 AUD
    {
      pattern: /\$([\d,]+(\.\d+)?)\s*AUD/,
      parse: numStr => parseFloat(numStr.replace(/,/g, '')),
      format: num => `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} AUD`
    },

    // 加元 \$399.90 CAD
    {
      pattern: /\$([\d,]+(\.\d+)?)\s*CAD/,
      parse: numStr => parseFloat(numStr.replace(/,/g, '')),
      format: num => `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} CAD`
    },

    // 保加利亚列弗 569.00 лв BGN
    {
      pattern: /([\d\s.,]+)\s*лв\s*BGN/,
      parse: numStr => parseFloat(numStr.replace(/,/g, '').replace(/\s/g, '')),
      format: num => {
        const [integerPart, decimalPart] = num.toFixed(2).split('.')
        return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart} лв BGN`
      }
    }
  ]

  // 寻找匹配的处理器
  const handler = currencyHandlers.find(h => h.pattern.test(originalPrice))

  if (!handler) return originalPrice // 无匹配处理器返回原价

  // 提取并解析价格
  const match = originalPrice.match(handler.pattern)
  const originalNumber = handler.parse(match[1])
  if (isNaN(originalNumber)) return originalPrice

  // 计算折扣价
  const discounted = originalNumber * (1 - discount)

  // 格式化输出
  return handler.format(discounted)
}

// 测试用例
// console.log(calculateDiscountedPrice('€284,90 EUR', 0.2)) // €227,92 EUR
// console.log(calculateDiscountedPrice('261,30 Kč', 0.2)) // 209,04 Kč
// console.log(calculateDiscountedPrice('$319.90 USD', 0.2)) // $255.92 USD
// console.log(calculateDiscountedPrice('210,00 zł PLN', 0.2)) // 168,00 zł PLN
// console.log(calculateDiscountedPrice('¥210 JPY', 0.1)) // ¥189 JPY
// console.log(calculateDiscountedPrice('251 SEK', 0.15)) // 213.35 SEK
// console.log(calculateDiscountedPrice('£309 GBP', 0.2)) // £247.20 GBP
// console.log(calculateDiscountedPrice('569.00 лв BGN', 0.2)) // 455.20 лв BGN
// console.log(calculateDiscountedPrice('$334.90 AUD', 0.2)) // $267.92 AUD
// console.log(calculateDiscountedPrice('$399.90 CAD', 0.2)) // $319.92 CAD
// console.log(calculateDiscountedPrice('168,00 DKK', 0.2)) // 134,40 DKK
// console.log(calculateDiscountedPrice('600 Ft', 0.2)) // 480 Ft
// console.log(calculateDiscountedPrice('447,00 lei RON', 0.2)) // 357,60 lei RON

// console.log('---------------下面是千位-----------------')

// console.log(calculateDiscountedPrice('€2.352,70 EUR', 0.4)) // €1.411,62 EUR
// console.log(calculateDiscountedPrice('5.121,00 Kč', 0.4)) // 3.072,60 Kč
// console.log(calculateDiscountedPrice('$4,474.30 USD', 0.4)) // $2,684.58 USD
// console.log(calculateDiscountedPrice('3.210,00 zł PLN', 0.4)) // 1.926,00 zł PLN
// console.log(calculateDiscountedPrice('¥22,210 JPY', 0.4)) // ¥13,326 JPY
// console.log(calculateDiscountedPrice('3 251 SEK', 0.4)) // 1 950.60 SEK
// console.log(calculateDiscountedPrice('£2,309 GBP', 0.4)) // £1,385.40 GBP
// console.log(calculateDiscountedPrice('3,569.00 лв BGN', 0.4)) // 2,141.40 лв BGN
// console.log(calculateDiscountedPrice('$6,334.90 AUD', 0.4)) //$3,800.94 AUD
// console.log(calculateDiscountedPrice('$2,399.90 CAD', 0.4)) // $1,439.94 CAD
// console.log(calculateDiscountedPrice('2.168,00 DKK', 0.4)) // 1.300,80 DKK
// console.log(calculateDiscountedPrice('116.600 Ft', 0.4)) // 69.960 Ft
// console.log(calculateDiscountedPrice('5.447,00 lei RON', 0.4)) // 3.268,20 lei RON
